import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, ChevronRight, Sparkles, Feather, Plus, Info, Lock, GitFork,
  Trophy, Tent, Medal, Users,
} from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { axiosInstance } from '../services/api';
import { getUser } from '../utils/secureStorage';
import { getChildStageChapters } from '../services/schoolYearsAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const CH_COLORS = ['#6B8E23', '#8B7355', '#556B2F', '#7B6B8E', '#8E6B23'];

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');
const CATEGORY_ICONS = { club: Users, scouts: Tent, team_sport: Trophy, individual_sport: Medal };
const CATEGORY_TILE_COLORS = { team_sport: '#E65100', scouts: '#6E531A', club: '#556B2F', individual_sport: '#556B2F' };

// ─── Stage Chapter Card (9b — expandable, matches ChapterStation) ───
function StageCard({ stage, navigate, personId, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isCurrent = stage.status === 'current';
  const isFuture = stage.status === 'future';
  const isPast = stage.status === 'past';
  const count = stage.storyCount || 0;
  const previews = stage.previews || [];

  // Future stages with no stories — not expandable
  if (isFuture && count === 0) {
    return (
      <div style={{
        border: '1.5px dashed #9A9F8A', borderRadius: 12,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', minHeight: 52,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: '#EFEDE3',
          border: '2px solid #C9C4AE', color: '#7F846F', fontSize: 13, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{stage.number}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15.5, color: '#4A4F40', fontFamily: FONT_B }}>{stage.title}</span>
          <span style={{ fontSize: 13.5, color: '#7F846F', fontFamily: FONT_B, marginLeft: 8 }}>{stage.dateLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      border: expanded ? '2px solid #3E4F22' : '1.5px solid #D6D1BD',
      borderRadius: 14, overflow: 'hidden',
    }}>
      {/* Header row */}
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${stage.title}, ${stage.dateLabel}, ${isCurrent ? 'current stage, ' : ''}${count} ${count === 1 ? 'story' : 'stories'}`}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 18px', minHeight: 60, border: 'none', cursor: 'pointer',
          background: expanded ? '#F1F5E8' : isCurrent ? '#F7FAF0' : '#fff',
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: FONT_H, fontWeight: 700, fontSize: 14.5,
          ...(isCurrent || expanded
            ? { background: '#3E4F22', color: '#fff' }
            : isPast
              ? { background: '#F1F5E8', border: '2px solid #3E4F22', color: '#3E4F22' }
              : { background: '#fff', border: '2px solid #C9C4AE', color: '#7F846F' }
          ),
        }}>{stage.number}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16.5, color: '#2C2F26', fontFamily: FONT_B }}>
            {stage.title}
            {isCurrent && (
              <span style={{
                fontFamily: FONT_B, fontSize: 13, fontWeight: 700, color: '#3E4F22',
                background: '#F1F5E8', border: '1.5px solid #3E4F22', borderRadius: 999,
                padding: '1px 9px', marginLeft: 8, verticalAlign: '1px',
              }}>Now</span>
            )}
          </div>
          <div style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B }}>
            {stage.dateLabel} · {count} {count === 1 ? 'story' : 'stories'}
          </div>
        </div>
        <ChevronRight size={22} color="#3E4F22" style={{
          transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)',
          flexShrink: 0, transition: 'transform .15s',
        }} />
      </button>

      {/* Expanded body — story previews */}
      {expanded && (
        <div style={{ padding: '12px 18px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {previews.map(mem => {
            const thumbUrl = mem.thumbnail_url
              ? (mem.thumbnail_url.startsWith('http') ? mem.thumbnail_url : `${API_ORIGIN}${mem.thumbnail_url}`)
              : null;
            return (
              <a key={mem.id} href={`/stories/${mem.id}`}
                onClick={e => { e.preventDefault(); navigate(`/stories/${mem.id}`); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: 12,
                  border: '1.5px solid #E4E1D2', borderRadius: 12, textDecoration: 'none', color: 'inherit',
                }}
              >
                <div style={{
                  width: 92, height: 68, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                  background: '#EFEDE3', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {thumbUrl ? (
                    <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  ) : (
                    <BookOpen size={24} color="#3F4436" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 17, color: '#2C2F26', fontFamily: FONT_B }}>{mem.title}</div>
                  <div style={{ fontSize: 14.5, color: '#4A4F40', fontFamily: FONT_B }}>{mem.year}</div>
                </div>
                <ChevronRight size={22} color="#3F4436" style={{ flexShrink: 0 }} />
              </a>
            );
          })}
          {count > 2 && (
            <button onClick={() => navigate(`/my-life/child/${personId}/school-years`)} style={{
              background: 'none', border: 'none', color: '#3E4F22', fontSize: 15,
              fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
              textDecoration: 'underline', textUnderlineOffset: '3px',
              padding: '10px 2px', textAlign: 'left',
            }}>
              See all {count} stories in this chapter
            </button>
          )}
          {count === 0 && (
            <p style={{ fontSize: 14.5, color: '#7F846F', fontFamily: FONT_B, margin: 0 }}>
              No stories yet. <button onClick={() => navigate(`/tell-a-story?person=${personId}`)} style={{
                background: 'none', border: 'none', color: '#3E4F22', fontSize: 14.5,
                fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
                textDecoration: 'underline', textUnderlineOffset: '3px', padding: 0,
              }}>+ Add</button>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Activity Row (9b) ───
function ActivityRow({ activity, isLast, onClick }) {
  const Icon = CATEGORY_ICONS[activity.category] || Trophy;
  const tileColor = CATEGORY_TILE_COLORS[activity.category] || '#556B2F';
  const count = activity.storyCount || 0;

  return (
    <button
      onClick={onClick}
      aria-label={`${activity.name}, ${activity.meta}, ${count} ${count === 1 ? 'story' : 'stories'}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '12px 20px', minHeight: 72, width: '100%',
        border: 'none', cursor: 'pointer', textAlign: 'left',
        borderBottom: isLast ? 'none' : '1px solid #EDEBE0',
        background: 'transparent',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Icon tile */}
      <div style={{
        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
        background: tileColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={20} color="#fff" />
      </div>

      {/* Name + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26' }}>
          {activity.name}
        </div>
        <div style={{
          fontFamily: FONT_B, fontSize: 14.5, color: '#4A4F40',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {activity.meta}
        </div>
      </div>

      {/* Count */}
      <span style={{
        fontFamily: FONT_B, fontSize: 15, fontWeight: 700, color: '#3E4F22',
        minWidth: 60, textAlign: 'right', flexShrink: 0,
      }}>
        {count} {count === 1 ? 'story' : 'stories'}
      </span>

      <ChevronRight size={20} color="#7F846F" style={{ flexShrink: 0 }} />
    </button>
  );
}


export default function PersonLifePage() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [childChapters, setChildChapters] = useState(null);
  const currentUser = getUser() || {};
  const isSelf = personId === currentUser.person_id;

  useEffect(() => {
    if (!personId) return;
    setLoading(true);

    const familyId = localStorage.getItem('familyId');

    Promise.allSettled([
      axiosInstance.get(`/families/${familyId}/members/${personId}/`),
      axiosInstance.get(`/chapters/my_life_data/?person_id=${personId}`),
    ]).then(async ([memberRes, lifeRes]) => {
      if (memberRes.status === 'fulfilled') {
        const m = memberRes.value.data;
        setPerson({
          id: m.id,
          displayName: `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'A family member',
          firstName: m.first_name || '',
          relationship: m.relationship || 'Family member',
          birthYear: m.date_of_birth ? new Date(m.date_of_birth).getFullYear() : null,
          photo: m.photo ? (m.photo.startsWith('http') ? m.photo : `${API_BASE}${m.photo}`) : null,
          initial: (m.first_name || '?')[0].toUpperCase(),
          state: m.node_state || 'member',
          nodeState: m.node_state,
        });
      }

      // Only fetch child chapters for people age ≤ 20
      let isChildLayout = false;
      if (memberRes.status === 'fulfilled') {
        const dob = memberRes.value.data?.date_of_birth;
        if (dob) {
          const age = new Date().getFullYear() - new Date(dob).getFullYear();
          if (age <= 20) {
            const cd = await getChildStageChapters(personId).catch(() => null);
            if (cd && (cd.person?.kStartYear != null || cd.person?.notInSchool)) {
              isChildLayout = true;
              setChildChapters(cd);
              const total = cd.totalStories || 0;
              const stageCount = (cd.stages || []).length;
              setCounts({ chapters: stageCount, stories: total, milestones: 0 });
            }
          }
        }
      }

      if (lifeRes.status === 'fulfilled' && !isChildLayout) {
        const d = lifeRes.value.data;
        const profile = d.profile || {};
        const chs = d.chapters || [];
        setChapters(chs);

        const totalStories = chs.reduce((s, c) => s + (c.memory_count || 0), 0);
        const totalMilestones = chs.reduce((s, c) => s + (c.milestone_count || 0), 0);
        setCounts({
          chapters: chs.length,
          stories: totalStories,
          milestones: totalMilestones,
        });

        // Fill person from life data if member fetch failed
        if (!person && profile.first_name) {
          setPerson(prev => prev || {
            id: personId,
            displayName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
            firstName: profile.first_name || '',
            relationship: 'Family member',
            birthYear: profile.birth_year,
            photo: null,
            initial: (profile.first_name || '?')[0].toUpperCase(),
            state: 'member',
          });
        }
      }
    }).finally(() => setLoading(false));
  }, [personId]); // eslint-disable-line

  if (loading) {
    return (
      <HubLayout activeView="life">
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '40px 24px', textAlign: 'center', color: '#828A76' }}>Loading...</div>
      </HubLayout>
    );
  }

  if (!person) {
    return (
      <HubLayout activeView="life">
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: FONT_H }}>Person not found</h2>
          <button onClick={() => navigate(-1)} style={{ background: '#556B2F', color: '#fff', padding: '10px 20px', borderRadius: 999, border: 'none', cursor: 'pointer', marginTop: 16 }}>Go back</button>
        </div>
      </HubLayout>
    );
  }

  const hasContent = counts && (counts.stories > 0 || counts.milestones > 0);
  const isFormerOrLeaving = person.state === 'former' || person.state === 'leaving';
  const useChildLayout = childChapters != null;

  const PROMPTS = [
    `What's your favorite memory of ${person.firstName}?`,
    `What did ${person.firstName} teach you?`,
    `Describe a time ${person.firstName} made you laugh.`,
    `What would you want ${person.firstName} to know?`,
  ];

  return (
    <HubLayout activeView="life">
      {/* Masthead */}
      <div style={{ background: '#4A5D23', padding: '22px 40px 26px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <button onClick={() => navigate('/family-hub')} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            color: '#C7D89B', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_B, padding: 0, marginBottom: 16,
          }}>
            <ArrowLeft size={16} /> Back to My Family
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            {/* Avatar */}
            <div style={{
              width: 62, height: 62, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              background: isFormerOrLeaving ? '#E4E2D6' : '#6B8E23',
              border: isFormerOrLeaving ? '1.5px solid #CFCCBA' : '3px solid rgba(255,255,255,.25)',
              color: '#fff', fontWeight: 700, fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isFormerOrLeaving ? null : person.photo ? (
                <img src={person.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : person.initial}
            </div>

            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 27, color: '#fff', margin: 0, lineHeight: 1.15 }}>
                The Life of {person.firstName}
              </h1>
              <div style={{ fontSize: 15, color: '#DCE5C4', fontFamily: FONT_B, marginTop: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
                {person.displayName} · {person.relationship}
                {(person.state === 'former' || person.state === 'never') && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)',
                    borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,.7)',
                  }}>
                    <GitFork size={11} /> Tree only
                  </span>
                )}
              </div>
            </div>

            {!isFormerOrLeaving && (
              <button onClick={() => navigate(`/tell-a-story?person=${personId}`)} style={{
                display: 'flex', alignItems: 'center', gap: 7, background: '#E65100', color: '#fff',
                fontWeight: 700, fontSize: 15, padding: '14px 22px', borderRadius: 999, border: 'none',
                cursor: 'pointer', fontFamily: FONT_B, minHeight: 48, whiteSpace: 'nowrap',
              }}>
                <Plus size={17} /> Write a story about {person.firstName}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 60px' }}>

        {/* ─── Has content or child layout ─── */}
        {(hasContent || useChildLayout) ? (
          <>
            {/* Count strip */}
            {counts && (
              <div style={{
                background: '#fff', border: '1px solid #E8E4D8', borderRadius: 14,
                display: 'flex', overflow: 'hidden', marginBottom: 24,
                boxShadow: '0 1px 2px rgba(54,60,40,.06)',
              }}>
                {[
                  { n: counts.chapters, l: 'Chapters' },
                  { n: counts.stories, l: 'Stories' },
                  ...(counts.milestones > 0 ? [{ n: counts.milestones, l: 'Milestones' }] : []),
                ].filter(s => s.n > 0).map((s, i, arr) => (
                  <div key={s.l} style={{
                    flex: 1, padding: '16px 20px', textAlign: 'center',
                    borderRight: i < arr.length - 1 ? '1px solid #EFEEE5' : 'none',
                  }}>
                    <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 24, color: '#556B2F' }}>{s.n}</div>
                    <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B }}>{s.l}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ─── 9b: Stage chapters (child with school years) ─── */}
            {useChildLayout ? (
              <>
                {/* Stage chapters */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 23, color: '#2C2F26', margin: 0 }}>
                      {person.firstName}'s life in chapters
                    </h2>
                    {(() => {
                      const withStories = childChapters.stages.filter(s => s.storyCount > 0);
                      return (
                        <span style={{ fontSize: 14.5, color: '#4A4F40', fontFamily: FONT_B }}>
                          {withStories.length} of {childChapters.stages.length} chapters {withStories.length === 1 ? 'has' : 'have'} stories
                        </span>
                      );
                    })()}
                  </div>

                  {/* Stages with stories — expandable cards */}
                  {(() => {
                    const withStories = childChapters.stages.filter(s => s.storyCount > 0 || s.status === 'current');
                    const withoutStories = childChapters.stages.filter(s => s.storyCount === 0 && s.status !== 'current');
                    return (
                      <>
                        {withStories.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                            {withStories.map(stage => (
                              <StageCard
                                key={stage.key}
                                stage={stage}
                                navigate={navigate}
                                personId={personId}
                                defaultExpanded={stage.status === 'current'}
                              />
                            ))}
                          </div>
                        )}

                        {/* Empty stages — compact dashed grid */}
                        {withoutStories.length > 0 && (
                          <div style={{ marginBottom: 24 }}>
                            <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26', margin: '0 0 4px' }}>
                              Chapters waiting for their first story
                            </h3>
                            <p style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B, margin: '0 0 12px' }}>
                              Tap one to add a memory to it.
                            </p>
                            <div style={{
                              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                              gap: 8,
                            }}>
                              {withoutStories.map(stage => (
                                <StageCard key={stage.key} stage={stage} navigate={navigate} personId={personId} />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Activities: "Things [Name] does" */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <Trophy size={20} color="#556B2F" />
                    <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0 }}>
                      Things {person.firstName} does
                    </h2>
                  </div>
                  <div style={{
                    background: '#fff', border: '1px solid #E4E1D2', borderRadius: 16, overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(54,60,40,.06)',
                  }}>
                    {childChapters.activities?.length > 0 ? (
                      <>
                        {childChapters.activities.map((act, i) => (
                          <ActivityRow
                            key={act.id}
                            activity={act}
                            isLast={i === childChapters.activities.length - 1 && false}
                            onClick={() => navigate(`/my-life/child/${personId}/school-years?view=activities`)}
                          />
                        ))}
                        {/* + Add an activity */}
                        <div style={{
                          padding: '0 20px', minHeight: 56,
                          display: 'flex', alignItems: 'center',
                          borderTop: '1px solid #EDEBE0',
                        }}>
                          <button
                            onClick={() => navigate(`/tell-a-story?person=${personId}`)}
                            style={{
                              fontFamily: FONT_B, fontSize: 15.5, fontWeight: 700,
                              color: '#3E4F22', background: 'none', border: 'none',
                              cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '12px 0', minHeight: 44,
                            }}
                          >
                            <Plus size={16} /> Add an activity
                          </button>
                        </div>
                      </>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        <button
                          onClick={() => navigate(`/tell-a-story?person=${personId}`)}
                          style={{
                            fontFamily: FONT_B, fontSize: 15.5, fontWeight: 700,
                            color: '#3E4F22', background: 'none', border: 'none',
                            cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '12px 0', minHeight: 44, margin: '0 auto',
                          }}
                        >
                          <Plus size={16} /> Add an activity
                        </button>
                        <p style={{
                          fontFamily: FONT_B, fontSize: 14, color: '#7F846F', margin: '4px 0 0',
                        }}>
                          Clubs, scouts and sports show here once a story is tagged.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* ─── Adult chapters (existing) ─── */
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <BookOpen size={18} color="#556B2F" />
                  <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0 }}>
                    {person.firstName}'s life in chapters
                  </h2>
                </div>
                <div style={{
                  background: '#fff', border: '1px solid #E8E4D8', borderRadius: 16, overflow: 'hidden',
                  boxShadow: '0 1px 2px rgba(54,60,40,.06)',
                }}>
                  {chapters.map((ch, i) => {
                    const count = (ch.memory_count || 0) + (ch.milestone_count || 0);
                    const era = `${ch.start_year || '?'} – ${ch.end_year || 'Present'}`;
                    return (
                      <div key={ch.id || i} onClick={() => navigate(`/my-life/chapters/${ch.id}`)} style={{
                        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                        borderBottom: i < chapters.length - 1 ? '1px solid #EFEEE5' : 'none',
                        cursor: 'pointer',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF3'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', background: CH_COLORS[i % CH_COLORS.length],
                          color: '#fff', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>{ch.number || i + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#2C2F26' }}>{ch.title}</div>
                          <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B }}>
                            {era}
                            {ch.starter_prompt && <span style={{ marginLeft: 8 }}><Sparkles size={12} color="#B5760A" style={{ verticalAlign: '-1px' }} /> {ch.starter_prompt.slice(0, 50)}</span>}
                          </div>
                        </div>
                        <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 16, color: count > 0 ? '#556B2F' : '#B0AE9C', minWidth: 24, textAlign: 'right' }}>
                          {count > 0 ? count : '—'}
                        </span>
                        <ChevronRight size={16} color="#C4CBB4" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          /* ─── Zero-story state (1b) ─── */
          <div style={{ marginBottom: 28 }}>
            <div style={{
              background: '#fff', border: '1px solid #E8E4D8', borderRadius: 16, padding: '48px 32px',
              textAlign: 'center', boxShadow: '0 1px 2px rgba(54,60,40,.06)',
            }}>
              <div style={{
                width: 74, height: 74, borderRadius: '50%', background: '#F3EFE1',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
              }}>
                <Feather size={32} color="#B5760A" />
              </div>
              <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 23, color: '#2C2F26', marginBottom: 8 }}>
                {person.firstName}'s story hasn't been written yet
              </h2>
              <p style={{ fontSize: 16.5, color: '#5A6150', fontFamily: FONT_B, maxWidth: 420, margin: '0 auto 24px', lineHeight: 1.5 }}>
                You remember things about {person.firstName.toLowerCase() === person.firstName ? 'them' : person.firstName} that nobody else does. A few sentences is a beginning.
              </p>
              {!isFormerOrLeaving && (
                <button onClick={() => navigate(`/tell-a-story?person=${personId}`)} style={{
                  background: '#E65100', color: '#fff', fontWeight: 700, fontSize: 15,
                  padding: '15px 28px', borderRadius: 999, border: 'none', cursor: 'pointer',
                  fontFamily: FONT_B, minHeight: 56,
                }}>
                  Write the first story
                </button>
              )}

              {/* Question prompts */}
              {!isFormerOrLeaving && (
                <div style={{ borderTop: '1px solid #EFEEE5', marginTop: 28, paddingTop: 20 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#828A76', fontFamily: FONT_B, marginBottom: 12 }}>
                    Or start from a question
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
                    {PROMPTS.slice(0, 3).map((prompt, i) => (
                      <button key={i} onClick={() => navigate(`/tell-a-story?person=${personId}`)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, background: '#FBFBF3',
                        border: '1px solid #E6E4D8', borderRadius: 999, padding: '10px 18px',
                        fontSize: 14, color: '#5A6150', cursor: 'pointer', fontFamily: FONT_B,
                      }}>
                        <Sparkles size={14} color="#B5760A" /> {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* About section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Info size={16} color="#828A76" />
            <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#5A6150', margin: 0 }}>About {person.firstName}</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
            {person.birthYear && (
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#A3A895', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4, fontFamily: FONT_B }}>Born</div>
                <div style={{ fontSize: 16.5, color: '#2C2F26', fontFamily: FONT_B }}>{person.birthYear}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#A3A895', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4, fontFamily: FONT_B }}>Relationship</div>
              <div style={{ fontSize: 16.5, color: '#2C2F26', fontFamily: FONT_B }}>{person.relationship}</div>
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#A3A895', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4, fontFamily: FONT_B }}>In the family tree</div>
              <div style={{ fontSize: 16.5, color: '#2C2F26', fontFamily: FONT_B }}>
                <GitFork size={14} style={{ verticalAlign: '-2px' }} /> On the tree
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#A3A895', fontFamily: FONT_B }}>
            <Lock size={13} /> Email, phone and account details are no longer shown here — they live in Settings.
          </div>
        </div>
      </div>

      <style>{`
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        @media (max-width: 640px) {
          [style*="padding: 22px 40px"] { padding: 16px 16px 20px !important; }
          [style*="padding: 28px 24px"] { padding: 20px 14px 40px !important; }
        }
      `}</style>
    </HubLayout>
  );
}
